import React, { useState, useEffect, useRef, useCallback } from 'react';
import LoadingIndicator from '../components/shared/LoadingIndicator';
import { Dialog, Classes, Icon, Button, Tooltip, Position } from '@blueprintjs/core';
import { FilledButton, TextButton } from '../components/buttons-20250224T111359Z-001/buttons';
import ConfirmationDialog from './ConfirmationDialog';
import FeedbackCompetencyDialog from './FeedbackCompetencyDialog';
import ScrollableDropdown from '../components/shared/ScrollableDropdown';
import Flexbox from 'flexbox-react';
import Delete from "../assets/Delete.svg";
import axios from 'axios';
import Cookies from 'js-cookie';
import ToastStore from '../stores/ToastStore';
import '../css/Feedback360.css';
import { COMPETENCY_CONFIG } from "../static/Constant"
import AddOptionDialog from '../components/feedback_360_settings/AddOptionDialog';
import FeedbackRaterTypeDialog from '../components/feedback_360_settings/FeedbackRaterTypeDialog';


const formatCompetencyLabel = (tag) => {
  if (!tag) return '';
  const lowerTag = String(tag).toLowerCase();
  if (COMPETENCY_CONFIG[lowerTag]) return COMPETENCY_CONFIG[lowerTag].label;
  return String(tag)
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getCompetencyClass = (tag) => {
  if (!tag) return 'chip-manager';
  const lowerTag = String(tag).toLowerCase();
  if (COMPETENCY_CONFIG[lowerTag]) return COMPETENCY_CONFIG[lowerTag].class;

  const classes = ['chip-manager', 'chip-peer', 'chip-skip', 'chip-report', 'chip-self'];
  let hash = 0;
  for (let i = 0; i < lowerTag.length; i++) {
    hash = lowerTag.charCodeAt(i) + ((hash << 5) - hash);
  }
  return classes[Math.abs(hash) % classes.length];
};

const getTypeToggleStyle = (isSelected) => ({
  flex: 1,
  textAlign: 'center',
  padding: '8px 6px',
  border: `1px solid ${isSelected ? '#1870d5' : '#DFE1E6'}`,
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: 600,
  color: isSelected ? '#1459aa' : '#5E6C84',
  backgroundColor: isSelected ? '#DEEBFF' : 'transparent',
  cursor: 'pointer'
});

const getRaterPillStyle = (rater) => {
  const value = String(rater.id || rater.title || '');

  let hash = 0;

  for (let i = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % raterColors.length;
  const color = raterColors[index];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 9px',
    borderRadius: '18px',
    backgroundColor: color.bg,
    color: color.text,
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  };
};

const raterColors = [
  { bg: '#DEEBFF', text: '#1870D5' },
  { bg: '#FFFAE6', text: '#FFAB00' },
  { bg: '#FFEBE6', text: '#FF5630' },
  { bg: '#E3FCEF', text: '#006644' },
  { bg: '#EAE6FF', text: '#403294' },
  { bg: '#E6FCFF', text: '#00A3BF' },
  { bg: '#FFEBE6', text: '#BF2600' },
  { bg: '#F4F5F7', text: '#42526E' }
];



const getApiUrl = (endpoint) => {
  const apiEnv = Cookies.get('apiEnv');
  const base = apiEnv ? apiEnv.replace(/\/+$/, '') : '';
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${base}${cleanEndpoint}`;
};

const mapQuestionFromApi = (item, competencyLookup = {}) => {
  const tag = item.competency_tag || item.competency_tag_id || item.competency_id || null;
  // competencyLookup maps id -> { value: id, label: title }
  const lookup = tag && competencyLookup[tag] ? competencyLookup[tag] : null;
  // Prefer server-provided competency_title when present
  const serverTitle = item.competency_title || (item.competency && item.competency.title) || null;
  const label = serverTitle || (lookup ? lookup.label : formatCompetencyLabel(tag));
  const chipClass = getCompetencyClass(lookup ? lookup.label : (serverTitle || tag));
  const isRating = item.answer_type === 'rating_scale';

  return {
    id: String(item.id),
    text: item.title || '',
    title: item.title || '',
    answer_type: item.answer_type || 'rating_scale',
    type: isRating ? 'Rating scale · 1–5' : 'Open text',
    competency_id: tag,
    competencyClass: chipClass,
    competency: label,
    visibility: Array.isArray(item.visibility) ? item.visibility : ['self', 'manager', 'peer', 'report']
  };
};

const Feedback360View = () => {
  // Goal Cycle State
  const [cycleOptions, setCycleOptions] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [loadingCycles, setLoadingCycles] = useState(false);

  // Question Bank State
  const [feedbackQuestions, setFeedbackQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [feedbackSearchQuery, setFeedbackSearchQuery] = useState('');

  // Competency and Rater category 
  const [isCompRaterDialogOpen, setCompRaterDialogOpen] = useState(false);

  // Competency manager state (fetched from server)
  const [isCompetencyModalOpen, setIsCompetencyModalOpen] = useState(false);
  const [managedCompetencies, setManagedCompetencies] = useState([]);

  // Competency List State
  const [competencyOptions, setCompetencyOptions] = useState([]);
  const [loadingCompetencies, setLoadingCompetencies] = useState(false);

  // Rater manager state
  const [isRaterTypeModalOpen, setIsRaterTypeModalOpen] = useState(false);
  const [raterTypesList, setRaterTypesList] = useState([]);
  const [loadingRaterTypes, setLoadingRaterTypes] = useState(false);

  // Sections State
  const [feedbackSections, setFeedbackSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);

  // Pagination & Infinite Scroll State
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const loadingQuestionsRef = useRef(false);
  const feedbackSearchQueryRef = useRef('');

  // Question Modal State
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackModalMode, setFeedbackModalMode] = useState('add'); // 'add' or 'edit'
  const [currentEditingQuestionId, setCurrentEditingQuestionId] = useState(null);
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false);

  // Form State & Validation Errors
  const [questionFormData, setQuestionFormData] = useState({
    title: '',
    answer_type: 'rating_scale',
    competency_id: 'communication',
    visibility: []
  });

  // Section Modal State
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [sectionModalMode, setSectionModalMode] = useState('add'); // 'add' or 'edit'
  const [currentEditingSectionId, setCurrentEditingSectionId] = useState(null);
  const [sectionFormData, setSectionFormData] = useState({ title: '' });
  const [isSubmittingSection, setIsSubmittingSection] = useState(false);

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: null, data: null, isDeleting: false });

  const searchTimeoutRef = useRef(null);

  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { loadingQuestionsRef.current = loadingQuestions; }, [loadingQuestions]);
  useEffect(() => { feedbackSearchQueryRef.current = feedbackSearchQuery; }, [feedbackSearchQuery]);

  useEffect(() => {
    fetchRaterTypes();
  }, []);

  // Fetch Goal Cycles 
  useEffect(() => {
    const fetchGoalCycles = async () => {
      setLoadingCycles(true);
      try {
        const url = getApiUrl('/goal_settings/goal_cycle_list');
        const response = await axios.get(url);
        let goalCycleOptions = [];
        const rawCycles = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.goal_settings)
            ? response.data.goal_settings
            : [];

        goalCycleOptions = rawCycles.map((item) => ({
          id: String(item?.id || item?.goal_setting_id),
          title: item?.title || item?.goal_setting_title || item?.name || 'Unknown Cycle',
          raw: item,
        }));

        if (goalCycleOptions.length > 0) {
          setCycleOptions(goalCycleOptions);
          setSelectedCycle(goalCycleOptions[0]); // First cycle selected by default
        }
      } catch (err) {
        console.error('Error fetching goal cycle list:', err);
      } finally {
        setLoadingCycles(false);
      }
    };

    fetchGoalCycles();
  }, []);

  // Fetch rater list

  const fetchRaterTypes = async () => {
    setLoadingRaterTypes(true);
    try {
      const url = getApiUrl('/rater');
      const response = await axios.get(url);
      const list = Array.isArray(response?.data)
        ? response.data
        : response?.data?.rater_types || [];

      setRaterTypesList(list);
    } catch (err) {
      console.error('Error fetching rater types:', err);
    } finally {
      setLoadingRaterTypes(false);
    }
  };

  // Fetch Competencies List
  useEffect(() => {
    const fetchCompetencies = async () => {
      setLoadingCompetencies(true);
      try {
        const url = getApiUrl('/competencies');
        const response = await axios.get(url);
        const list = Array.isArray(response?.data) ? response.data : response?.data?.competencies || [];

        const dropdownOptions = list.map((item) => ({ value: item.id, label: item.title || item.value || item.key }));
        const managed = list.map((item) => ({ id: item.id || null, value: item.id || String(item.key || item.value), label: item.title || item.value || item.key }));

        setCompetencyOptions(dropdownOptions);
        setManagedCompetencies(managed);
      } catch (err) {
        console.error('Error fetching competency list:', err);
      } finally {
        setLoadingCompetencies(false);
      }
    };

    fetchCompetencies();
  }, []);

  // Refresh competencies from server (used after create/update/delete)
  const refreshCompetencies = async () => {
    setLoadingCompetencies(true);
    try {
      const url = getApiUrl('/competencies');
      const response = await axios.get(url);
      const list = Array.isArray(response?.data) ? response.data : response?.data?.competencies || [];
      const dropdownOptions = list.map((item) => ({ value: item.id, label: item.title || item.value || item.key }));
      const managed = list.map((item) => ({ id: item.id || null, value: item.id || String(item.key || item.value), label: item.title || item.value || item.key }));
      setCompetencyOptions(dropdownOptions);
      setManagedCompetencies(managed);
    } catch (err) {
      console.error('Error refreshing competency list:', err);
    } finally {
      setLoadingCompetencies(false);
    }
  };

  // Fetch Sections for Selected Goal Cycle
  const fetchFeedbackSections = useCallback(async (goalSettingId) => {
    if (!goalSettingId) return;
    setLoadingSections(true);
    try {
      const url = getApiUrl('/feedback_sections');
      const response = await axios.get(url, { params: { goal_setting_id: goalSettingId } });

      let rawList = [];
      if (Array.isArray(response?.data?.feedback_sections)) {
        rawList = response.data.feedback_sections;
      } else if (Array.isArray(response?.data)) {
        rawList = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        rawList = response.data.data;
      }

      const mappedSections = rawList.map(sec => ({
        id: String(sec.id),
        title: sec.title || 'Untitled Section',
        order: sec.order || 0,
        questions: Array.isArray(sec.questions) ? sec.questions.map(q => {
          const fq = q.feedback_question || {};
          const tag = fq.competency_id || 'communication';
          const label = formatCompetencyLabel(tag);
          const chipClass = getCompetencyClass(tag);
          const isRating = fq.answer_type === 'rating_scale';
          return {
            id: String(q.id),
            feedback_question_id: String(fq.id || q.feedback_question_id || q.id),
            text: fq.title || '',
            title: fq.title || '',
            answer_type: fq.answer_type || 'rating_scale',
            type: isRating ? 'Rating scale · 1–5' : 'Open text',
            competency_id: tag,
            competencyClass: chipClass,
            competency: label,
            visibility: Array.isArray(fq.visibility) ? fq.visibility : ['self', 'manager', 'peer', 'report']
          };
        }) : []
      }));

      setFeedbackSections(mappedSections);

      setActiveSectionId(prev => {
        if (prev && mappedSections.some(s => s.id === prev)) return prev;
        return mappedSections.length > 0 ? mappedSections[0].id : null;
      });
    } catch (err) {
      console.error('Error fetching feedback sections:', err);
      setFeedbackSections([]);
      setActiveSectionId(null);
    } finally {
      setLoadingSections(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCycle?.id) {
      fetchFeedbackSections(selectedCycle.id);
    }
  }, [selectedCycle, fetchFeedbackSections]);

  // Question Bank Fetching
  const fetchFeedbackQuestions = useCallback(async (searchTerm = '') => {
    setLoadingQuestions(true);
    loadingQuestionsRef.current = true;
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    hasMoreRef.current = true;

    try {
      const url = getApiUrl('/feedback_questions');
      const params = {
        page: 1,
        per_page: 10
      };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await axios.get(url, { params });
      const responseData = response?.data;
      const rawList = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
          ? responseData.data
          : [];

      const compLookup = {};
      competencyOptions.forEach(o => { compLookup[o.value] = o; });
      const mappedList = rawList.map(item => mapQuestionFromApi(item, compLookup));
      setFeedbackQuestions(mappedList);

      const meta = responseData?.meta;
      const moreAvailable = meta ? (meta.page < meta.total_pages) : (mappedList.length >= 10);
      setHasMore(moreAvailable);
      hasMoreRef.current = moreAvailable;
    } catch (err) {
      console.error('Error fetching feedback questions:', err);
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to fetch feedback questions';
      ToastStore.showToast(errMsg, 'danger');
      setFeedbackQuestions([]);
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      setLoadingQuestions(false);
      loadingQuestionsRef.current = false;
    }
  }, []);

  const fetchMoreFeedbackQuestions = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current || loadingQuestionsRef.current) return;

    setLoadingMore(true);
    loadingMoreRef.current = true;

    const nextPage = pageRef.current + 1;
    try {
      const url = getApiUrl('/feedback_questions');
      const params = {
        page: nextPage,
        per_page: 10
      };
      const currentSearch = feedbackSearchQueryRef.current;
      if (currentSearch && currentSearch.trim()) {
        params.search = currentSearch.trim();
      }

      const response = await axios.get(url, { params });
      const responseData = response?.data;
      const rawList = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
          ? responseData.data
          : [];

      const compLookup = {};
      competencyOptions.forEach(o => { compLookup[o.value] = o; });
      const mappedList = rawList.map(item => mapQuestionFromApi(item, compLookup));

      if (mappedList.length > 0) {
        setFeedbackQuestions(prev => [...prev, ...mappedList]);
        setPage(nextPage);
        pageRef.current = nextPage;
      }

      const meta = responseData?.meta;
      const moreAvailable = meta ? (meta.page < meta.total_pages) : (mappedList.length >= 10);
      setHasMore(moreAvailable);
      hasMoreRef.current = moreAvailable;
    } catch (err) {
      console.error('Error fetching more feedback questions:', err);
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, []);

  const handleBankScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 40) {
      if (hasMoreRef.current && !loadingMoreRef.current && !loadingQuestionsRef.current) {
        fetchMoreFeedbackQuestions();
      }
    }
  };

  useEffect(() => {
    fetchFeedbackQuestions();
  }, [fetchFeedbackQuestions]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setFeedbackSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchFeedbackQuestions(query);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const openDeleteBankQuestionConfirm = (qId) => {
    setConfirmDialog({ isOpen: true, type: 'bankQuestion', data: qId, isDeleting: false });
  };

  const openDeleteSectionConfirm = (sectionId) => {
    setConfirmDialog({ isOpen: true, type: 'section', data: sectionId, isDeleting: false });
  };

  const openDeleteQuestionConfirm = (sectionId, qId, feedbackQuestionId) => {
    setConfirmDialog({ isOpen: true, type: 'question', data: { sectionId, qId, feedbackQuestionId }, isDeleting: false });
  };

  const handleConfirmDelete = async () => {
    if (confirmDialog.type === 'bankQuestion') {
      const qId = confirmDialog.data;
      setConfirmDialog(prev => ({ ...prev, isDeleting: true }));
      try {
        const url = getApiUrl(`/feedback_questions/${qId}`);
        const response = await axios.delete(url);
        const successMsg = response?.data?.message || 'Feedback question deleted successfully';
        ToastStore.showToast(successMsg, 'success');
        await fetchFeedbackQuestions(feedbackSearchQuery);
      } catch (err) {
        console.error('Error deleting feedback question:', err);
        const errMsg = err?.response?.data?.message || err?.message || 'Failed to delete feedback question';
        ToastStore.showToast(errMsg, 'danger');
      } finally {
        setConfirmDialog({ isOpen: false, type: null, data: null, isDeleting: false });
      }
    } else if (confirmDialog.type === 'section') {
      const sectionId = confirmDialog.data;
      if (!selectedCycle?.id) return;
      setConfirmDialog(prev => ({ ...prev, isDeleting: true }));
      try {
        const url = getApiUrl(`/feedback_sections/${sectionId}`);
        const response = await axios.delete(url, { params: { goal_setting_id: selectedCycle.id } });
        const successMsg = response?.data?.message || 'Deleted Successfully';
        ToastStore.showToast(successMsg, 'success');

        const remainingSections = feedbackSections.filter(sec => sec.id !== sectionId);
        setFeedbackSections(remainingSections);
        if (activeSectionId === sectionId) {
          setActiveSectionId(remainingSections.length > 0 ? remainingSections[0].id : null);
        }
      } catch (err) {
        console.error('Error deleting section:', err);
        const errMsg = err?.response?.data?.message || err?.message || 'Failed to delete section';
        ToastStore.showToast(errMsg, 'danger');
      } finally {
        setConfirmDialog({ isOpen: false, type: null, data: null, isDeleting: false });
      }
    } else if (confirmDialog.type === 'question') {
      const { sectionId, qId, feedbackQuestionId } = confirmDialog.data;
      if (!selectedCycle?.id) return;
      setConfirmDialog(prev => ({ ...prev, isDeleting: true }));
      try {
        const url = getApiUrl('/feedback_sections/remove_question');
        const response = await axios.post(url, {
          goal_setting_id: selectedCycle.id,
          feedback_question_id: feedbackQuestionId || qId,
          feedback_section_id: sectionId
        });
        const successMsg = response?.data?.message || 'Question removed from section';
        ToastStore.showToast(successMsg, 'success');
        await fetchFeedbackSections(selectedCycle.id);
      } catch (err) {
        console.error('Error removing question from section:', err);
        const errMsg = err?.response?.data?.message || err?.message || 'Failed to remove question';
        ToastStore.showToast(errMsg, 'danger');
      } finally {
        setConfirmDialog({ isOpen: false, type: null, data: null, isDeleting: false });
      }
    }
  };

  const openSectionModal = (mode, section = null) => {
    setSectionModalMode(mode);
    if (mode === 'edit' && section) {
      setCurrentEditingSectionId(section.id);
      setSectionFormData({ title: section.title || '' });
    } else {
      setCurrentEditingSectionId(null);
      setSectionFormData({ title: '' });
    }
    setIsSectionModalOpen(true);
  };

  const closeSectionModal = () => {
    setIsSectionModalOpen(false);
  };

  const handleSaveSection = async () => {
    if (!sectionFormData.title || !sectionFormData.title.trim()) {
      ToastStore.showToast('Section title cannot be empty', 'warning');
      return;
    }
    if (!selectedCycle?.id) {
      ToastStore.showToast('No goal cycle selected', 'warning');
      return;
    }

    setIsSubmittingSection(true);
    try {
      if (sectionModalMode === 'add') {
        const url = getApiUrl('/feedback_sections');
        const response = await axios.post(url, {
          title: sectionFormData.title.trim(),
          goal_setting_id: selectedCycle.id
        });
        ToastStore.showToast(response?.data?.message || 'Created Successfully', 'success');

        const res = await axios.get(getApiUrl('/feedback_sections'), { params: { goal_setting_id: selectedCycle.id } });
        let rawList = res?.data?.feedback_sections || res?.data || [];
        if (Array.isArray(rawList)) {
          const compLookup = {};
          competencyOptions.forEach(o => { compLookup[o.value] = o; });

          const mappedSections = rawList.map(sec => ({
            id: String(sec.id),
            title: sec.title || 'Untitled Section',
            order: sec.order || 0,
            questions: Array.isArray(sec.questions) ? sec.questions.map(q => {
              const fq = q.feedback_question || {};
              const tag = fq.competency_tag || fq.competency_tag_id || fq.competency_id || null;
              const lookup = tag && compLookup[tag] ? compLookup[tag] : null;
              const label = lookup ? lookup.label : formatCompetencyLabel(tag);
              const chipClass = getCompetencyClass(lookup ? lookup.label : tag);
              const isRating = fq.answer_type === 'rating_scale';
              return {
                id: String(q.id),
                feedback_question_id: String(fq.id || q.feedback_question_id || q.id),
                text: fq.title || '',
                title: fq.title || '',
                answer_type: fq.answer_type || 'rating_scale',
                type: isRating ? 'Rating scale · 1–5' : 'Open text',
                competency_id: tag,
                competencyClass: chipClass,
                competency: label,
                visibility: Array.isArray(fq.visibility) ? fq.visibility : ['self', 'manager', 'peer', 'report']
              };
            }) : []
          }));
          setFeedbackSections(mappedSections);

          const createdId = response?.data?.id || response?.data?.feedback_section?.id;
          if (createdId && mappedSections.some(s => s.id === String(createdId))) {
            setActiveSectionId(String(createdId));
          } else if (mappedSections.length > 0) {
            setActiveSectionId(mappedSections[mappedSections.length - 1].id);
          }
        }
      } else if (sectionModalMode === 'edit') {
        const url = getApiUrl(`/feedback_sections/${currentEditingSectionId}`);
        const response = await axios.put(url, {
          title: sectionFormData.title.trim(),
          goal_setting_id: selectedCycle.id
        });
        ToastStore.showToast(response?.data?.message || 'Updated Successfully', 'success');

        setFeedbackSections(prev => prev.map(sec =>
          sec.id === currentEditingSectionId ? { ...sec, title: sectionFormData.title.trim() } : sec
        ));
        setActiveSectionId(currentEditingSectionId);
      }
      closeSectionModal();
    } catch (err) {
      console.error('Error saving feedback section:', err);
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to save section';
      ToastStore.showToast(errMsg, 'danger');
    } finally {
      setIsSubmittingSection(false);
    }
  };


  const openFeedbackModal = (mode, question = null) => {
    setFeedbackModalMode(mode);

    const defaultTag =
      competencyOptions.length > 0
        ? competencyOptions[0].value
        : 'communication';

    if (mode === 'edit' && question) {
      setCurrentEditingQuestionId(question.id);

      setQuestionFormData({
        title: question.title || question.text || '',
        answer_type:
          question.answer_type ||
          (question.type === 'Open text' ? 'open_text' : 'rating_scale'),

        competency_id:
          question.competency_id ||
          question.competency_tag ||
          defaultTag,

        visibility:
          Array.isArray(question.visibility)
            ? [...question.visibility]
            : []
      });
    } else {
      setCurrentEditingQuestionId(null);

      setQuestionFormData({
        title: '',
        answer_type: 'rating_scale',
        competency_id: defaultTag,
        visibility: []
      });
    }

    setIsFeedbackModalOpen(true);
  };

  const closeFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  const validateQuestionForm = () => {
    let errorMsg = null;
    if (!questionFormData.title || !questionFormData.title.trim()) {
      errorMsg = 'Question text is required';
    } else if (!questionFormData.answer_type) {
      errorMsg = 'Answer type is required';
    } else if (!questionFormData.competency_id) {
      errorMsg = 'Competency tag is required';
    } else if (!Array.isArray(questionFormData.visibility) || questionFormData.visibility.length === 0) {
      errorMsg = 'At least one visibility role must be selected';
    }

    if (errorMsg) {
      ToastStore.showToast(errorMsg, 'warning');
      return false;
    }
    return true;
  };

  const handleSaveFeedbackQuestion = async () => {
    if (!validateQuestionForm()) {
      return;
    }

    setIsSubmittingQuestion(true);
    const payload = {
      title: questionFormData.title.trim(),
      answer_type: questionFormData.answer_type,
      competency_id: questionFormData.competency_id,
      // removinf isDefault key (asked by backend)
      visibility: questionFormData.visibility.map(({ id, title }) => ({
        id,
        title
      }))
    };

    try {
      if (feedbackModalMode === 'add') {
        const url = getApiUrl('/feedback_questions');
        const response = await axios.post(url, payload);
        const msg = response?.data?.message || 'Feedback question created successfully';
        ToastStore.showToast(msg, 'success');
      } else if (feedbackModalMode === 'edit') {
        const url = getApiUrl(`/feedback_questions/${currentEditingQuestionId}`);
        const response = await axios.put(url, payload);
        const msg = response?.data?.message || 'Feedback question updated successfully';
        ToastStore.showToast(msg, 'success');

        const compLookup = {};
        competencyOptions.forEach(o => { compLookup[o.value] = o; });
        const updatedMapped = mapQuestionFromApi({ id: currentEditingQuestionId, ...payload }, compLookup);
        setFeedbackSections(prevSections => prevSections.map(sec => ({
          ...sec,
          questions: sec.questions.map(q =>
            q.feedback_question_id === currentEditingQuestionId ? { ...q, ...updatedMapped } : q
          )
        })));
      }

      closeFeedbackModal();
      await fetchFeedbackQuestions(feedbackSearchQuery);
    } catch (err) {
      console.error('Error saving feedback question:', err);
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to save feedback question';
      ToastStore.showToast(errMsg, 'danger');
    } finally {
      setIsSubmittingQuestion(false);
    }
  };

  const toggleQuestionVisibility = (role) => {
    setQuestionFormData(prev => {
      const nextVis = prev.visibility.includes(role)
        ? prev.visibility.filter(r => r !== role)
        : [...prev.visibility, role];

      return { ...prev, visibility: nextVis };
    });
  };

  const toggleRaterCategory = (rater) => {
    setQuestionFormData(prev => {
      const isSelected = prev.visibility.some(
        item => item.id === rater.id
      );

      return {
        ...prev,
        visibility: isSelected
          ? prev.visibility.filter(item => item.id !== rater.id)
          : [...prev.visibility, rater]
      };
    });
  };

  const [addingQuestionId, setAddingQuestionId] = useState(null);

  // Add Question into Section
  const handleAddQuestionToFeedbackSection = async (question) => {
    if (!selectedCycle?.id) {
      ToastStore.showToast('Please select a goal cycle first', 'warning');
      return;
    }

    if (feedbackSections.length === 0) {
      ToastStore.showToast('Please create a section first', 'warning');
      openSectionModal('add');
      return;
    }

    let targetSectionId = activeSectionId;
    if (!targetSectionId || !feedbackSections.some(s => s.id === targetSectionId)) {
      targetSectionId = feedbackSections[0].id;
      setActiveSectionId(targetSectionId);
    }

    const targetSection = feedbackSections.find(s => s.id === targetSectionId);
    if (targetSection && targetSection.questions.some(q => q.feedback_question_id === question.id || q.id === question.id)) {
      ToastStore.showToast('This question is already in the section.', 'warning');
      return;
    }

    setAddingQuestionId(question.id);
    try {
      const url = getApiUrl('/feedback_sections/add_question');
      const response = await axios.post(url, {
        goal_setting_id: selectedCycle.id,
        feedback_question_id: question.id,
        feedback_section_id: targetSectionId
      });

      const successMsg = response?.data?.message || 'Question added to the feedback section successfully';
      ToastStore.showToast(successMsg, 'success');
      await fetchFeedbackSections(selectedCycle.id);
    } catch (err) {
      console.error('Error adding question to feedback section:', err);
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to add question to section';
      ToastStore.showToast(errMsg, 'danger');
    } finally {
      setAddingQuestionId(null);
    }
  };



  return (
    <div className="feedback-builder-container">
      <div className="topbar">
        <div className="topbar-title-section">
          <h1 className="topbar-title">360 Feedback Management</h1>
          <p className="topbar-description">Configure feedback sections, manage questions, and set responder visibility for performance cycles.</p>
        </div>

        <div className="feedback-topbar-actions">
          <div className="goal-cycle-dropdown-container">
            {loadingCycles ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#5E6C84', fontSize: '13px' }}>
                <LoadingIndicator size={16} />
                <span>Loading cycles...</span>
              </div>
            ) : cycleOptions.length > 0 ? (
              <ScrollableDropdown
                width={240}
                label={selectedCycle?.title || 'Select Goal Cycle'}
                items={cycleOptions}
                getKey={(item) => item.id}
                getLabel={(item) => item.title}
                onSelect={(item) => {
                  setSelectedCycle(item);
                }}
              />
            ) : null}
          </div>
          {/* <FilledButton
            onClick={() => setIsCompetencyModalOpen(true)}
            text="+ Add Competency"
          /> */}
          <FilledButton text="Feedback Settings" onClick={() => setCompRaterDialogOpen(true)} />
        </div>
      </div>

      <div className="builder-grid">
        <div className="card bank-card">
          <div className="bank-head">
            <h3 style={{ fontSize: "16px" }}>Question bank</h3>
            {loadingQuestions && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#5E6C84', fontSize: '12px', flexShrink: 0 }}>
                <img style={{ height: '25px', width: '25px' }} src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif" alt="Loading..." />
                <span>Loading...</span>
              </div>
            )}
          </div>
          <input
            className="search-input"
            placeholder="Search by Question..."
            value={feedbackSearchQuery}
            onChange={handleSearchChange}
          />
          <div id="bank-list" className="thin-scroll" onScroll={handleBankScroll}>
            {feedbackQuestions.map((q, idx) => (
              <div key={`${q.id}-${idx}`} className="bank-item">
                <div className="bank-item-top" style={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
                  <Tooltip portalContainer={document.body} content={q.text} position={Position.TOP} targetClassName="line-clamp-target" popoverClassName="question-tooltip" hoverCloseDelay={50} openOnTargetFocus={false}>
                    <p className="line-clamp-2" style={{ margin: 0 }}>{q.text}</p>
                  </Tooltip>
                </div>
                <div
                  className="bank-item-actions"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    minWidth: 0,
                  }}
                >
                  <Tooltip
                    portalContainer={document.body}
                    content={q.competency}
                    position={Position.TOP}
                    hoverCloseDelay={50}
                    openOnTargetFocus={false}
                  >
                    <span
                      className={`chip ${q.competencyClass}`}
                      style={{
                        display: 'inline-flex',
                        maxWidth: '155px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flexShrink: 1,
                        cursor: 'default',
                      }}
                    >
                      {q.competency}
                    </span>
                  </Tooltip>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <Tooltip portalContainer={document.body} content="Edit question" position={Position.TOP} hoverCloseDelay={50} openOnTargetFocus={false}>
                      <Button
                        icon={
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7.63704 21.2615H2.51904L2.69554 16.1885L16.1595 3.1285C16.4168 2.87888 16.761 2.73903 17.1194 2.73847C17.4779 2.73791 17.8225 2.87669 18.0805 3.1255L21.059 5.9975C21.1916 6.12538 21.2973 6.27848 21.3698 6.44778C21.4424 6.61708 21.4803 6.79918 21.4815 6.98337C21.4827 7.16756 21.447 7.35012 21.3766 7.52033C21.3062 7.69053 21.2025 7.84495 21.0715 7.9745L7.63704 21.2615ZM3.47254 20.3385H7.25854L20.4235 7.3195C20.4672 7.27634 20.5018 7.22488 20.5253 7.16815C20.5488 7.11142 20.5607 7.05056 20.5603 6.98916C20.56 6.92776 20.5473 6.86705 20.5231 6.81061C20.499 6.75416 20.4637 6.70313 20.4195 6.6605L17.441 3.7885C17.3552 3.70538 17.2403 3.65891 17.1208 3.65891C17.0013 3.65891 16.8864 3.70538 16.8005 3.7885L3.60054 16.5885L3.47254 20.3385Z"
                              fill="#707070"
                            />
                            <path
                              d="M18.8589 9.5245L14.4719 5.21L15.2029 4.491L19.5899 8.8055L18.8589 9.5245Z"
                              fill="#707070"
                            />
                          </svg>
                        }
                        minimal
                        onClick={() => openFeedbackModal('edit', q)}
                        className="edit-question-btn no-hover-btn"
                      />
                    </Tooltip>
                    <Tooltip
                      portalContainer={document.body}
                      content="Add Question to selected section"
                      position={Position.TOP}
                      hoverCloseDelay={50}
                      openOnTargetFocus={false}
                    >
                      <div
                        className="add-to-section-wrapper"
                        onClick={() => {
                          if (addingQuestionId !== q.id) {
                            handleAddQuestionToFeedbackSection(q);
                          }
                        }}
                      >
                        {addingQuestionId === q.id ? (
                          <>
                            <img style={{ height: '16px', width: '16px' }} src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif" alt="Loading..." />
                            <span>Adding...</span>
                          </>
                        ) : (
                          <>
                            <span>Add</span>
                            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="#FFFFFF">
                              <path d="M2.25 7.25h8.69L7.72 4.03a1.06 1.06 0 0 1 1.5-1.5l5.03 5.03a1.06 1.06 0 0 1 0 1.5l-5.03 5.03a1.06 1.06 0 0 1-1.5-1.5l3.22-3.22H2.25a1.06 1.06 0 1 1 0-2.12Z" />
                            </svg>
                          </>
                        )}
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
            ))}

            {loadingMore && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '12px 0', gap: '8px', color: '#5E6C84', fontSize: '13px' }}>
                <img style={{ height: '35px', width: '35px' }} src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif" alt="Loading..." />
              </div>
            )}

            {!loadingQuestions && !loadingMore && feedbackQuestions.length > 0 && !hasMore && (
              <div style={{ textAlign: 'center', color: '#8993A4', padding: '12px 0', fontSize: '12px' }}>
                All questions loaded
              </div>
            )}

            {!loadingQuestions && feedbackQuestions.length === 0 && (
              <div style={{ textAlign: 'center', color: '#8993A4', padding: '16px 0', fontSize: '13px' }}>
                No questions found.
              </div>
            )}
          </div>
          <button className="btn-text" style={{ marginTop: '12px' }} onClick={() => openFeedbackModal('add')}>+ Add Question</button>
        </div>

        <div className="sections-container">
          <div id="sections-list" className="thin-scroll">
            {loadingSections && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '30px 0', gap: '10px', color: '#5E6C84' }}>
                <Flexbox flexGrow={1} flexDirection="column" justifyContent="center" alignItems="center">
                  <img style={{ height: '35px', width: '35px' }} src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif" alt="Loading..." />
                </Flexbox>
              </div>
            )}

            {!loadingSections && feedbackSections.map(section => {
              const isActive = section.id === activeSectionId;
              return (
                <div
                  key={section.id}
                  className={`card section-block ${isActive ? 'active-section' : ''}`}
                  onClick={() => setActiveSectionId(section.id)}
                >
                  <div className="section-head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <h3>{section.title}</h3>
                      {/* {isActive && (
                        <span className="active-section-badge">
                          Activei
                        </span>
                      )} */}

                      {isActive && (
                        <span className="active-section-badge">
                          SELECTED FOR ADDING
                        </span>
                      )}
                    </div>
                    <div className="section-head-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Tooltip portalContainer={document.body} content="Edit section" position={Position.TOP} hoverCloseDelay={50} openOnTargetFocus={false}>
                        <Button
                          icon={
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7.63704 21.2615H2.51904L2.69554 16.1885L16.1595 3.1285C16.4168 2.87888 16.761 2.73903 17.1194 2.73847C17.4779 2.73791 17.8225 2.87669 18.0805 3.1255L21.059 5.9975C21.1916 6.12538 21.2973 6.27848 21.3698 6.44778C21.4424 6.61708 21.4803 6.79918 21.4815 6.98337C21.4827 7.16756 21.447 7.35012 21.3766 7.52033C21.3062 7.69053 21.2025 7.84495 21.0715 7.9745L7.63704 21.2615ZM3.47254 20.3385H7.25854L20.4235 7.3195C20.4672 7.27634 20.5018 7.22488 20.5253 7.16815C20.5488 7.11142 20.5607 7.05056 20.5603 6.98916C20.56 6.92776 20.5473 6.86705 20.5231 6.81061C20.499 6.75416 20.4637 6.70313 20.4195 6.6605L17.441 3.7885C17.3552 3.70538 17.2403 3.65891 17.1208 3.65891C17.0013 3.65891 16.8864 3.70538 16.8005 3.7885L3.60054 16.5885L3.47254 20.3385Z"
                                fill="#1870d5"
                              />
                              <path
                                d="M18.8589 9.5245L14.4719 5.21L15.2029 4.491L19.5899 8.8055L18.8589 9.5245Z"
                                fill="#1870d5"
                              />
                            </svg>
                          }
                          minimal
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveSectionId(section.id);
                            openSectionModal('edit', section);
                          }}
                          className="edit-section-btn no-hover-btn"
                        />
                      </Tooltip>
                      <Tooltip portalContainer={document.body} content="Delete section" position={Position.TOP} hoverCloseDelay={50} openOnTargetFocus={false}>
                        <Button icon={<img src={Delete} alt="Delete Icon" style={{ width: 18, height: 18 }} />}
                          minimal={true}
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteSectionConfirm(section.id);
                          }} />
                      </Tooltip>
                    </div>
                  </div>
                  {
                    section.questions.map(q => (
                      <div key={q.id} className="q-row">
                        <div className="q-top">
                          <div style={{ flex: 1, minWidth: 0, paddingRight: '16px', overflow: 'hidden' }}>
                            <Tooltip portalContainer={document.body} content={q.text} position={Position.TOP} targetClassName="line-clamp-target" popoverClassName="question-tooltip" hoverCloseDelay={50} openOnTargetFocus={false}>
                              <p className="q-text line-clamp-2" style={{ margin: 0 }}>{q.text}</p>
                            </Tooltip>
                            <p className="q-type">{q.type}</p>
                          </div>
                          <div className="visibility-row" style={{ alignItems: 'center' }}>
                            {/* {q.visibility.map(rater => (
                              <span
                                key={rater.id}
                                style={{
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '5px 10px',
                                  borderRadius: '18px',
                                  backgroundColor: '#EAF4FF',
                                  color: '#1870D5',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}
                              >
                                {rater.title}
                              </span>
                            ))}                                           */}
                            {q.visibility.map(rater => (
                              <span
                                key={rater.id}
                                style={getRaterPillStyle(rater)}
                              >
                                {rater.title}
                              </span>
                            ))}
                            <div className="question-delete-action">
                              <Tooltip portalContainer={document.body} content="Delete question" position={Position.TOP} hoverCloseDelay={50} openOnTargetFocus={false}>
                                <Button icon={<img src={Delete} alt="Delete Icon" style={{ width: 18, height: 18 }} />}
                                  minimal={true}
                                  onClick={(e) => { e.stopPropagation(); openDeleteQuestionConfirm(section.id, q.id, q.feedback_question_id); }}
                                />
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  }
                  {
                    section.questions.length === 0 && (
                      <div style={{ padding: '16px 20px', color: '#8993A4', fontSize: '13px', fontStyle: 'italic', textAlign: 'center' }}>
                        No questions in this section yet. Click "+ Add" on a question in the bank to add it here.
                      </div>
                    )
                  }
                </div>
              );
            })}

            {!loadingSections && feedbackSections.length === 0 && (
              <div className="empty-sections-placeholder" onClick={() => openSectionModal('add')}>
                <div className="empty-icon-circle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </div>
                <h4>No feedback sections yet</h4>
                <p>Click to add first section and start building 360 feedback form.</p>
                <button className="btn btn-primary" style={{ marginTop: '14px' }}>+ Add section</button>
              </div>
            )}
          </div>

          {!loadingSections && feedbackSections.length > 0 && (
            <div className="add-section-footer">
              <div className="add-section" onClick={() => openSectionModal('add')}>
                + Add section
              </div>
            </div>
          )}
        </div>
      </div>

      <AddOptionDialog
        isOpen={isCompRaterDialogOpen}
        onClose={() => setCompRaterDialogOpen(false)}
        onSelect={(type) => {
          setCompRaterDialogOpen(false);
          if (type === 'competency') {
            setIsCompetencyModalOpen(true);
          }
          if (type === 'direct_report') {
            setIsRaterTypeModalOpen(true);
          }
        }}
      />

      <FeedbackCompetencyDialog
        isOpen={isCompetencyModalOpen}
        onClose={() => setIsCompetencyModalOpen(false)}
        options={managedCompetencies}
        onChange={refreshCompetencies}
      />

      <FeedbackRaterTypeDialog
        isOpen={isRaterTypeModalOpen}
        onClose={() => setIsRaterTypeModalOpen(false)}
        options={raterTypesList}
        loading={loadingRaterTypes}
        onChange={fetchRaterTypes}
      />

      {/* Modal for Add/Edit Question in Question Bank */}
      <Dialog
        isOpen={isFeedbackModalOpen}
        onClose={closeFeedbackModal}
        title={feedbackModalMode === 'add' ? 'Add Question' : 'Edit Question'}
        portalContainer={document.body}
      >
        <Flexbox flexDirection="column" padding="20px" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="field">
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Question
            </label>
            <textarea
              className="bp3-input"
              placeholder="e.g. Communicates changes and blockers proactively"
              value={questionFormData.title}
              onChange={(e) => {
                const val = e.target.value;
                setQuestionFormData(prev => ({ ...prev, title: val }));
              }}
              style={{ width: '100%', height: '64px', fontSize: '14px' }}
            />
          </div>

          <div className="field" style={{ marginTop: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Answer type
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div
                style={getTypeToggleStyle(questionFormData.answer_type === 'rating_scale')}
                onClick={() => {
                  setQuestionFormData(prev => ({ ...prev, answer_type: 'rating_scale' }));
                }}
              >
                Rating scale
              </div>
              <div
                style={getTypeToggleStyle(questionFormData.answer_type === 'open_text')}
                onClick={() => {
                  setQuestionFormData(prev => ({ ...prev, answer_type: 'open_text' }));
                }}
              >
                Open text
              </div>
            </div>
          </div>

          <div className="field" style={{ marginTop: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Competency tag
            </label>
            <div className="bp3-select" style={{ width: '100%' }}>
              <select
                value={questionFormData.competency_id}
                onChange={(e) => {
                  const val = e.target.value;
                  setQuestionFormData(prev => ({ ...prev, competency_id: val }));
                }}
                style={{ width: '100%', fontSize: '14px' }}
              >
                {competencyOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="field" style={{ marginTop: '20px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '8px'
              }}
            >
              Rater Category
            </label>

            <div
              className="bp3-select"
              style={{
                width: '100%',
                minHeight: '40px'
              }}
            >
              <select
                value=""
                onChange={(e) => {
                  const selectedId = e.target.value;

                  const selectedRater = raterTypesList.find(
                    opt => opt.id === selectedId
                  );

                  if (selectedRater) {
                    toggleRaterCategory(selectedRater);
                  }
                }}
                style={{
                  width: '100%',
                  fontSize: '14px'
                }}
              >
                <option value="">
                  {questionFormData.visibility.length === 0
                    ? 'Select Rater Category'
                    : 'Add Rater Category'}
                </option>

                {raterTypesList
                  .filter(
                    opt =>
                      !questionFormData.visibility.some(
                        selected => selected.id === opt.id
                      )
                  )
                  .map(opt => (
                    <option key={opt.id} value={opt.id}>
                      {opt.title}
                    </option>
                  ))}
              </select>
            </div>

            {/* Selected Rater Categories */}
            {questionFormData.visibility.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  marginTop: '8px'
                }}
              >
                {questionFormData.visibility.map(rater => (
                  <span
                    key={rater.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '5px 10px',
                      borderRadius: '3px',
                      backgroundColor: '#EAF4FF',
                      color: '#1870D5',
                      fontSize: '12px',
                      fontWeight: 600
                    }}
                  >
                    {rater.title}

                    <Icon
                      icon="small-cross"
                      iconSize={12}
                      onClick={() => toggleRaterCategory(rater)}
                      style={{
                        cursor: 'pointer'
                      }}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          <Flexbox justifyContent="flex-start" style={{ marginTop: '20px', gap: '8px' }}>
            <FilledButton
              text={isSubmittingQuestion ? 'Saving...' : (feedbackModalMode === 'add' ? 'Save' : 'Save')}
              onClick={handleSaveFeedbackQuestion}
              disabled={isSubmittingQuestion}
            />
            <TextButton text="Cancel" onClick={closeFeedbackModal} disabled={isSubmittingQuestion} />
          </Flexbox>
        </Flexbox>
      </Dialog>

      {/* Modal for Add / Edit Section */}
      <Dialog
        isOpen={isSectionModalOpen}
        onClose={closeSectionModal}
        title={sectionModalMode === 'add' ? 'Add Section' : 'Edit Section'}
        portalContainer={document.body}
        style={{ paddingBottom: 10 }}
      >
        <Flexbox flexDirection="column" padding="20px">
          <div className="field">
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Section
            </label>
            <input
              className="bp3-input"
              type="text"
              placeholder="e.g. Leadership & Direction"
              value={sectionFormData.title}
              onChange={(e) => setSectionFormData({ title: e.target.value })}
              style={{ width: '100%', fontSize: '14px' }}
              autoFocus
            />
          </div>
          <Flexbox justifyContent="flex-start" style={{ marginTop: '20px' }}>
            <FilledButton
              text={isSubmittingSection ? 'Saving...' : (sectionModalMode === 'add' ? 'Save' : 'Save')}
              onClick={handleSaveSection}
              disabled={isSubmittingSection}
            />
            <TextButton text="Cancel" onClick={closeSectionModal} disabled={isSubmittingSection} />
          </Flexbox>
        </Flexbox>
      </Dialog>

      {
        (() => {
          let confirmItemName = '';
          try {
            if (confirmDialog.type === 'section') {
              const sId = String(confirmDialog.data || '');
              const s = feedbackSections.find(x => String(x.id) === sId);
              confirmItemName = s ? (s.title || '') : '';
            } else if (confirmDialog.type === 'question') {
              // when removing a question, show the section name only
              const data = confirmDialog.data || {};
              const sId = String(data.sectionId || '');
              const s = feedbackSections.find(x => String(x.id) === sId);
              confirmItemName = s ? (s.title || '') : '';
            }
          } catch (err) {
            confirmItemName = '';
          }

          const titleText = confirmDialog.type === 'question' ? 'Confirm Removal' : 'Confirm Deletion';
          let messageText = '';
          if (confirmDialog.type === 'bankQuestion') {
            messageText = 'Are you sure you want to delete this question from the question bank?';
          } else if (confirmDialog.type === 'section') {
            messageText = 'Are you sure you want to delete this section: ';
          } else if (confirmDialog.type === 'question') {
            messageText = 'Are you sure you want to remove this question from the section: ';
          }

          return (
            <ConfirmationDialog
              isOpen={confirmDialog.isOpen}
              onClose={() => {
                if (!confirmDialog.isDeleting) {
                  setConfirmDialog({ isOpen: false, type: null, data: null, isDeleting: false });
                }
              }}
              onConfirm={handleConfirmDelete}
              title={titleText}
              message={messageText}
              itemName={confirmItemName}
              confirmText={confirmDialog.isDeleting ? "Deleting..." : "Delete"}
              confirmIntent="danger"
              isSubmitting={confirmDialog.isDeleting}
            />
          );
        })()
      }

    </div >
  );
};

export default Feedback360View;


